import { c as _c } from "react/compiler-runtime";
import type { Language, Locale, OnboardingSessionConfig } from '@onefootprint/request-types';
import { Button, LinkButton, PageShell } from '@onefootprint/ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type DeviceInfo, isDesktopDevice } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import useCreateHandoffUrl from '../hooks/use-create-handoff-url';
import useD2pSession from '../hooks/use-d2p-session';
import useRequirementsTitle from '../hooks/use-requirements-title';
import type { TransferRequirements } from '../types';
import createAppclipUrl from '../utils/create-appclip-url';
import ProcessingOverlay from './processing-overlay';
const {
  logInfo,
  logWarn,
  logError
} = getLogger({
  name: 'idv2',
  location: 'new-tab-transfer'
});
type NewTabTransferProps = {
  authToken: string;
  config: OnboardingSessionConfig;
  device: DeviceInfo;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  missingRequirements: TransferRequirements;
  hasAppclipSupport: boolean;
  sandboxIdDocOutcome?: string;
  fromContinueOnMobile: boolean;
  onDone: () => void;
};
const NewTabTransfer = t0 => {
  const $ = _c(62);
  const {
    authToken,
    config,
    device,
    l10n,
    missingRequirements,
    hasAppclipSupport,
    sandboxIdDocOutcome,
    fromContinueOnMobile,
    onDone
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "transfer.newTabTransfer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t1);
  let t2;
  if ($[1] !== hasAppclipSupport || $[2] !== missingRequirements) {
    t2 = {
      missingRequirements,
      isContinuingOnDesktop: false,
      isAppClipProcessing: hasAppclipSupport
    };
    $[1] = hasAppclipSupport;
    $[2] = missingRequirements;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    title
  } = useRequirementsTitle(t2);
  const [openedTab, setOpenedTab] = useState();
  const [popupBlockedError, setPopupBlockedError] = useState();
  let t3;
  if ($[4] !== onDone || $[5] !== openedTab) {
    t3 = () => {
      openedTab?.close();
      onDone();
    };
    $[4] = onDone;
    $[5] = openedTab;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleD2pFinish = t3;
  let t4;
  if ($[7] !== openedTab) {
    t4 = () => {
      openedTab?.close();
    };
    $[7] = openedTab;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleD2pCancel = t4;
  let t5;
  if ($[9] !== authToken || $[10] !== device || $[11] !== handleD2pCancel || $[12] !== handleD2pFinish || $[13] !== l10n || $[14] !== sandboxIdDocOutcome) {
    t5 = {
      authToken,
      device,
      l10n,
      sandboxIdDocOutcome,
      onFinish: handleD2pFinish,
      onCancel: handleD2pCancel
    };
    $[9] = authToken;
    $[10] = device;
    $[11] = handleD2pCancel;
    $[12] = handleD2pFinish;
    $[13] = l10n;
    $[14] = sandboxIdDocOutcome;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const d2p = useD2pSession(t5);
  let t6;
  if ($[16] !== config || $[17] !== d2p.state.scopedAuthToken || $[18] !== fromContinueOnMobile || $[19] !== hasAppclipSupport || $[20] !== l10n.language) {
    t6 = createAppclipUrl({
      authToken: d2p.state.scopedAuthToken,
      onboardingConfig: config,
      language: l10n.language,
      hasAppclipSupport,
      fromContinueOnMobile
    });
    $[16] = config;
    $[17] = d2p.state.scopedAuthToken;
    $[18] = fromContinueOnMobile;
    $[19] = hasAppclipSupport;
    $[20] = l10n.language;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const appClip = t6;
  let t7;
  if ($[22] !== config || $[23] !== d2p.state.scopedAuthToken || $[24] !== fromContinueOnMobile || $[25] !== l10n.language || $[26] !== missingRequirements) {
    t7 = {
      authToken: d2p.state.scopedAuthToken,
      onboardingConfig: config,
      language: l10n.language,
      missingRequirements,
      fromContinueOnMobile
    };
    $[22] = config;
    $[23] = d2p.state.scopedAuthToken;
    $[24] = fromContinueOnMobile;
    $[25] = l10n.language;
    $[26] = missingRequirements;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const handoffUrl = useCreateHandoffUrl(t7);
  let t8;
  if ($[28] !== appClip.url) {
    t8 = () => {
      if (!appClip.url) {
        logError("Failed to launch App Clip: no URL");
        return;
      }
      logInfo(`Transfer opening App Clip with URL ${appClip.url}`);
      window.open(appClip.url, "_blank");
    };
    $[28] = appClip.url;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const handleOpenAppClip = t8;
  let t9;
  if ($[30] !== device || $[31] !== handoffUrl || $[32] !== t) {
    t9 = () => {
      setPopupBlockedError(undefined);
      if (!handoffUrl) {
        logError("Failed to open new tab: no URL");
        return;
      }
      const url = new URL(handoffUrl);
      const isDesktopOpener = isDesktopDevice(device);
      if (isDesktopOpener) {
        url.searchParams.set("source", "newtab");
      }
      const newTabUrl = url.toString();
      const tab = window.open(newTabUrl, "_blank", "height=800px,width=600px,location=no,menubar=no,status=no,toolbar=no,left=100px,top=100px");
      if (tab) {
        setOpenedTab(tab);
        logInfo(`Transfer new tab opened successfully to ${url.origin}${url.pathname}`, {
          sourceNewtab: isDesktopOpener
        });
        return;
      }
      logWarn("Transfer popup blocked while opening new tab");
      setPopupBlockedError(t(_temp));
    };
    $[30] = device;
    $[31] = handoffUrl;
    $[32] = t;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleOpenNewTab = t9;
  let t10;
  if ($[34] !== d2p || $[35] !== openedTab) {
    t10 = () => {
      d2p.cancel();
      openedTab?.close();
      setOpenedTab(undefined);
      d2p.regenerate();
      logInfo("Transfer cancelled");
    };
    $[34] = d2p;
    $[35] = openedTab;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const handleCancel = t10;
  const isProcessing = d2p.state.status === "in_progress";
  const supportsAppClip = appClip.isSupported && hasAppclipSupport;
  if (isProcessing) {
    let t11;
    if ($[37] !== supportsAppClip || $[38] !== t) {
      t11 = supportsAppClip ? t(_temp2) : t(_temp3);
      $[37] = supportsAppClip;
      $[38] = t;
      $[39] = t11;
    } else {
      t11 = $[39];
    }
    let t12;
    if ($[40] !== d2p.state.isCanceling || $[41] !== handleCancel || $[42] !== t11 || $[43] !== title) {
      t12 = <ProcessingOverlay title={title} subtitle={t11} onCancel={handleCancel} isCanceling={d2p.state.isCanceling} />;
      $[40] = d2p.state.isCanceling;
      $[41] = handleCancel;
      $[42] = t11;
      $[43] = title;
      $[44] = t12;
    } else {
      t12 = $[44];
    }
    return t12;
  }
  const isLoading = d2p.state.isGenerating || !d2p.state.scopedAuthToken;
  let t11;
  if ($[45] !== supportsAppClip || $[46] !== t) {
    t11 = supportsAppClip ? t(_temp4) : t(_temp5);
    $[45] = supportsAppClip;
    $[46] = t;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== supportsAppClip || $[49] !== t) {
    t12 = supportsAppClip ? t(_temp6) : t(_temp7);
    $[48] = supportsAppClip;
    $[49] = t;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  let t13;
  if ($[51] !== handleOpenAppClip || $[52] !== handleOpenNewTab || $[53] !== isLoading || $[54] !== popupBlockedError || $[55] !== supportsAppClip || $[56] !== t) {
    t13 = <div className="flex flex-col items-center gap-6">{supportsAppClip ? <><Button disabled={isLoading} fullWidth={true} onClick={handleOpenAppClip} size="large" variant="primary">{t(_temp8)}</Button><div className="flex flex-col items-center gap-2"><LinkButton onClick={handleOpenNewTab} disabled={isLoading}>{t(_temp9)}</LinkButton>{popupBlockedError && <p className="text-body-2 text-error text-center">{popupBlockedError}</p>}</div></> : <div className="flex flex-col items-center gap-2 w-full"><Button disabled={isLoading} fullWidth={true} onClick={handleOpenNewTab} size="large" variant="primary">{t(_temp10)}</Button>{popupBlockedError && <p className="text-body-2 text-error text-center">{popupBlockedError}</p>}</div>}</div>;
    $[51] = handleOpenAppClip;
    $[52] = handleOpenNewTab;
    $[53] = isLoading;
    $[54] = popupBlockedError;
    $[55] = supportsAppClip;
    $[56] = t;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== t11 || $[59] !== t12 || $[60] !== t13) {
    t14 = <PageShell title={t11} subtitle={t12}>{t13}</PageShell>;
    $[58] = t11;
    $[59] = t12;
    $[60] = t13;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  return t14;
};
export default NewTabTransfer;
function _temp($) {
  return $.popupBlocked;
}
function _temp2($_0) {
  return $_0.processingWithAppClipSubtitle;
}
function _temp3($_1) {
  return $_1.processingNoAppClipSubtitle;
}
function _temp4($_2) {
  return $_2.appclip.title;
}
function _temp5($_3) {
  return $_3.default.title;
}
function _temp6($_4) {
  return $_4.appclip.subtitle;
}
function _temp7($_5) {
  return $_5.default.subtitle;
}
function _temp8($_6) {
  return $_6.appclip.cta;
}
function _temp9($_7) {
  return $_7.appclip.skip;
}
function _temp10($_8) {
  return $_8.default.cta;
}